
import React from 'react';
import { CheckCircle, Users, Mail, ArrowRight, BookOpen, AlertCircle, Gift, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SectionLayout from '@/components/SectionLayout';

const SpotlightThanks = () => {
  return <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#003a70]/5 to-white">
      {/* SEO - Meta tags to prevent indexing */}
      <head>
        <title>Thank You for Your Purchase | AskSpot</title>
        <meta name="robots" content="noindex, nofollow" />
      </head>
      
      <main className="flex-grow">
        <SectionLayout paddingY="py-8 sm:py-12 md:py-16 lg:py-20" mobilePaddingY="py-6" backgroundColor="bg-transparent" containerWidth="medium" decorativeElements={<>
              {/* Success checkmarks - hidden on mobile for cleaner look */}
              <div className="absolute top-[20%] left-[10%] animate-float opacity-30 hidden lg:block">
                <CheckCircle className="text-green-500 h-8 w-8 fill-current" />
              </div>
              
              <div className="absolute top-[40%] right-[15%] animate-float opacity-25 hidden lg:block" style={{
          animationDelay: '1.5s'
        }}>
                <CheckCircle className="text-green-400 h-6 w-6 fill-current" />
              </div>
              
              {/* Gradient orbs - hidden on mobile and tablet */}
              <div className="absolute top-[30%] right-[25%] w-32 h-32 bg-gradient-to-br from-green-500/20 to-[#FEE6D8]/20 rounded-full blur-2xl animate-pulse opacity-60 hidden lg:block"></div>
              <div className="absolute bottom-[25%] left-[20%] w-40 h-40 bg-gradient-to-br from-[#003a70]/15 to-green-400/15 rounded-full blur-xl animate-float opacity-50 hidden lg:block" style={{
          animationDelay: '2s'
        }}></div>
            </>}>
          <div className="max-w-4xl mx-auto text-center px-3 sm:px-4">
            {/* Main Heading */}
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#003a70] mb-3 sm:mb-4 md:mb-6 leading-tight">
              Thank You for Your Purchase!
            </h1>
            
            {/* Subtitle */}
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8 md:mb-10 lg:mb-12 leading-relaxed px-2">
              Welcome to the <span className="font-semibold text-[#003a70]">The Genius Card Playbook</span> training program. You're now ready to start building your AI assistant business!
            </p>

            {/* Access Instructions Card */}
            <Card className="bg-white/80 backdrop-blur-sm border-2 border-[#FEE6D8]/50 shadow-xl mb-6 sm:mb-8 md:mb-10 lg:mb-12">
              <CardContent className="p-4 sm:p-6 md:p-8 lg:p-10">
                <div className="flex items-center justify-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                  <BookOpen className="text-[#003a70] h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 flex-shrink-0" />
                  <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-[#003a70] text-center">
                    How to Access Your Training
                  </h2>
                </div>
                
                <div className="space-y-4 sm:space-y-5 md:space-y-6 text-left max-w-2xl mx-auto mb-8 sm:mb-10">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#003a70] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs sm:text-sm">
                      1
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#003a70] mb-1 sm:mb-2 text-sm sm:text-base">Join Our Skool Community</h3>
                      <p className="text-gray-600 text-sm sm:text-base leading-relaxed">The Genius Card Playbook training can be accessed in the classroom area of our Skool community. You should have received an email invite from the AskSpot Skool Community. Click on the link in the email to unlock access to the course...</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#003a70] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs sm:text-sm">2</div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#003a70] mb-1 sm:mb-2 text-sm sm:text-base">Take Action NOW!</h3>
                      <p className="text-gray-600 text-sm sm:text-base leading-relaxed">Don't let this killer strategy and training collect digital dust! Dig in, go though the training and take action NOW!</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons - Now inside the card */}
                <div className="space-y-4 sm:space-y-5">
                  {/* Primary Action Button */}
                  <div>
                    <Button size="lg" className="w-full bg-gradient-to-r from-[#003a70] to-[#004a8a] hover:from-[#002a60] hover:to-[#003a70] text-white text-base sm:text-lg font-bold py-4 sm:py-5 px-6 sm:px-8 shadow-xl hover:shadow-2xl group transform hover:scale-[1.02] transition-all duration-300 rounded-xl border-2 border-[#003a70]/20" onClick={() => window.open('https://www.skool.com/askspot', '_blank')}>
                      <Users className="h-5 w-5 sm:h-6 sm:w-6 mr-3 flex-shrink-0" />
                      <span className="flex-1">Access Skool Community</span>
                      <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6 ml-3 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                    </Button>
                  </div>

                  {/* Secondary Actions */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <Button variant="outline" size="lg" className="border-2 border-[#003a70]/30 text-[#003a70] hover:bg-[#003a70] hover:text-white text-sm sm:text-base font-semibold py-3 sm:py-4 px-4 sm:px-6 transition-all duration-300 rounded-xl shadow-md hover:shadow-lg group" onClick={() => window.location.href = 'mailto:support@askspot.ai?subject=Launchpad Training Support'}>
                      <Mail className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" />
                      <span>Contact Support</span>
                    </Button>

                    <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-sm sm:text-base font-bold py-3 sm:py-4 px-4 sm:px-6 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 border-2 border-orange-400/30 rounded-xl group" onClick={() => window.open('https://app.askspot.ai/share-chatbot/6840539b27d635e3fc3d0bed?embed=true', '_blank')}>
                      <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" />
                      <span className="text-center leading-tight">Missing Skool Invite?</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Animated Pointer to Special Offer */}
            <div className="flex flex-col items-center mb-4 sm:mb-6">
              <div className="flex items-center gap-2 text-[#003a70] font-semibold text-sm sm:text-base mb-2 animate-pulse">
                <span>Don't miss this special offer below!</span>
              </div>
              <div className="animate-bounce">
                <ArrowDown className="h-6 w-6 sm:h-8 sm:w-8 text-[#003a70]" />
              </div>
            </div>

            {/* Special Offer Card */}
            <Card className="bg-gradient-to-r from-[#FEE6D8] to-[#FEE6D8]/80 border-2 border-[#003a70]/30 shadow-xl mb-6 sm:mb-8 md:mb-10 lg:mb-12 transform hover:scale-105 transition-all duration-300">
              <CardContent className="p-4 sm:p-6 md:p-8 lg:p-10">
                <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <Gift className="text-[#003a70] h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 flex-shrink-0" />
                  <h2 className="sm:text-xl md:text-2xl lg:text-3xl font-bold text-[#003a70] text-center leading-tight text-xl">
                    AskSpot Limited Time Special Offer
                  </h2>
                </div>
                
                <p className="text-base sm:text-lg md:text-xl text-[#003a70] mb-4 sm:mb-6 leading-relaxed px-2">
                  For a limited time claim a two month 100% FREE trial of AskSpot Unlimited.
                </p>
                
                <Button size="lg" className="w-full sm:w-auto bg-[#003a70] hover:bg-[#003a70]/90 text-white text-base sm:text-lg md:text-xl py-3 sm:py-4 md:py-5 px-6 sm:px-8 md:px-10 font-bold shadow-xl hover:shadow-2xl group transform hover:scale-105 transition-all duration-300 min-h-[48px]" onClick={() => window.open('https://askspot.ai/launchpad-offer', '_self')}>
                  <Gift className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="truncate">Click Here For Details</span>
                  <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 ml-2 sm:ml-3 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </SectionLayout>
      </main>
    </div>;
};

export default SpotlightThanks;
